import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Card } from "../ui";
import { TRACKING_INVALIDATION_COPY, type TrackingImpactVariant } from "./doesChangeImpactTracking";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

export interface InvalidationModalProps {
  variant: TrackingImpactVariant | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const InvalidationModal = ({ variant, onConfirm, onCancel }: InvalidationModalProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const bodyId = useId();

  useEffect(() => {
    if (!variant) {
      return undefined;
    }
    const card = cardRef.current;
    card?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancelRef.current();
        return;
      }
      if (event.key !== "Tab" || !card) {
        return;
      }
      const focusables = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        event.preventDefault();
        card.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [variant]);

  if (!variant || typeof document === "undefined") {
    return null;
  }

  const copy = TRACKING_INVALIDATION_COPY[variant];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <Card
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
        aria-describedby={bodyId}
        tabIndex={-1}
        className="relative z-10 flex w-full max-w-md flex-col gap-4 p-6 focus:outline-none"
      >
        <h2 className="text-lg font-semibold">{copy.title}</h2>
        <p id={bodyId} className="text-muted-foreground text-sm">
          {copy.body}
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            {copy.confirmLabel}
          </button>
        </div>
      </Card>
    </div>,
    document.body
  );
};

export { InvalidationModal };
